import React, { useState, useRef, useEffect } from 'react';
import Image from 'components/AppImage';
import Icon from 'components/AppIcon';
import { useNavigate } from 'react-router-dom';

const MODE_COLORS = {
  'text-to-game': 'var(--color-primary)',
  'prompt-to-mini-game': 'var(--color-secondary)',
  'text-to-animation': 'var(--color-accent)',
  'script-to-video': '#10B981',
  'music-to-video': '#F43F5E',
  'text-to-music': '#8B5CF6',
  'lip-sync': '#EC4899',
  'image-to-video': '#06B6D4',
};

const MODE_LABELS = {
  'text-to-game': 'Game',
  'prompt-to-mini-game': 'Mini Game',
  'text-to-animation': 'Animation',
  'script-to-video': 'Video',
  'music-to-video': 'Music Video',
  'text-to-music': 'Music',
  'lip-sync': 'Lip Sync',
  'image-to-video': 'Img to Video',
};

export default function GameCard({ game, onPlay, onEdit, onDuplicate, onPublish, onTogglePublish, onShare, onDelete, onRemix, onTrailer, onPublishPlatform, onMonetize, onEditVideo, onDownload }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const modeColor = game?.isMonkeyRide ? '#6366f1' : (MODE_COLORS?.[game?.mode] || 'var(--color-primary)');
  const modeLabel = game?.isMonkeyRide ? 'Endless Runner' : (MODE_LABELS?.[game?.mode] || 'Game');

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef?.current && !menuRef?.current?.contains(e?.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handlePlay = () => {
    if (game?.isMonkeyRide) {
      navigate('/monkey-ride');
      return;
    }
    onPlay(game);
  };

  return (
    <div
      className="rounded-2xl overflow-hidden flex flex-col group transition-all duration-200 hover:shadow-lg"
      style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)' }}
    >
      {/* Thumbnail */}
      <div className="relative overflow-hidden h-36 md:h-40 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950 dark:to-violet-950">
        <Image
          src={game?.thumbnail}
          alt={game?.thumbnailAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {/* Mode badge */}
        <span
          className="absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full font-semibold"
          style={{ background: modeColor, color: '#fff', fontFamily: 'var(--font-caption)' }}
        >
          {modeLabel}
        </span>
        {/* Published badge */}
        {game?.published && (
          <span
            className="absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"
            style={{ background: 'rgba(16,185,129,0.9)', color: '#fff', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="Globe" size={10} color="#fff" />
            Public
          </span>
        )}
        {/* Play overlay */}
        <button
          onClick={handlePlay}
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          style={{ background: 'rgba(0,0,0,0.4)' }}
          aria-label={`Play ${game?.title}`}
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--color-primary)' }}>
            <Icon name="Play" size={22} color="white" />
          </div>
        </button>
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold line-clamp-1 flex-1" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
            {game?.title}
          </h3>
          {/* Actions menu */}
          <div className="relative flex-shrink-0" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-[var(--color-muted)]"
              aria-label="More options"
            >
              <Icon name="MoreVertical" size={14} color="var(--color-muted-foreground)" />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-8 w-44 rounded-xl overflow-hidden z-50 animate-fade-in-up"
                style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-lg)' }}
              >
                {[
                  { label: 'Play', icon: 'Play', action: () => { handlePlay(); setMenuOpen(false); } },
                  { label: 'Edit Prompt', icon: 'Edit3', action: () => { onEdit(game); setMenuOpen(false); } },
                  { label: 'Remix', icon: 'Shuffle', action: () => { onRemix?.(game); setMenuOpen(false); } },
                  { label: 'Duplicate', icon: 'Copy', action: () => { onDuplicate(game); setMenuOpen(false); } },
                  { label: game?.published ? 'Unpublish' : 'Publish', icon: game?.published ? 'EyeOff' : 'Globe', action: () => { onPublish(game); setMenuOpen(false); } },
                  { label: 'Share', icon: 'Share2', action: () => { onShare(game); setMenuOpen(false); } },
                  { label: 'Download ZIP', icon: 'Download', action: () => { onDownload?.(game); setMenuOpen(false); } },
                  { label: 'Edit Video', icon: 'Scissors', action: () => { onEditVideo?.(game); setMenuOpen(false); } },
                  { label: 'Monetize', icon: 'DollarSign', action: () => { onMonetize?.(game); setMenuOpen(false); } },
                  { label: 'Auto Trailer', icon: 'Clapperboard', action: () => { onTrailer?.(game); setMenuOpen(false); } },
                  { label: 'Publish to...', icon: 'Send', action: () => { onPublishPlatform?.(game); setMenuOpen(false); } },
                  { label: 'Delete', icon: 'Trash2', action: () => { onDelete(game); setMenuOpen(false); }, danger: true },
                ]?.map((item) => (
                  <button
                    key={item?.label}
                    onClick={item?.action}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs transition-all hover:bg-[var(--color-muted)]"
                    style={{ color: item?.danger ? 'var(--color-error)' : 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
                  >
                    <Icon name={item?.icon} size={13} color={item?.danger ? 'var(--color-error)' : 'var(--color-muted-foreground)'} />
                    {item?.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <p className="text-xs line-clamp-2" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
          {game?.prompt}
        </p>

        <div className="flex items-center justify-between mt-auto pt-1">
          <span className="text-xs flex-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)' }}>
            {game?.createdAt}
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
              <Icon name="Eye" size={11} />
              <span style={{ fontFamily: 'var(--font-data)' }}>{(game?.views || 0)?.toLocaleString()}</span>
            </div>
            {/* Download button */}
            <button
              onClick={() => onDownload?.(game)}
              className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full transition-all hover:opacity-80"
              style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', border: '1px solid var(--color-border)', fontFamily: 'var(--font-caption)' }}
              title="Download as ZIP"
            >
              <Icon name="Download" size={10} />
              ZIP
            </button>
            <button
              onClick={() => onTogglePublish && onTogglePublish(game)}
              className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full transition-all hover:opacity-80"
              style={{
                background: game?.published ? 'rgba(16,185,129,0.1)' : 'var(--color-muted)',
                color: game?.published ? 'var(--color-success)' : 'var(--color-muted-foreground)',
                border: `1px solid ${game?.published ? 'rgba(16,185,129,0.3)' : 'var(--color-border)'}`,
                fontFamily: 'var(--font-caption)',
              }}
              title={game?.published ? 'Click to unpublish' : 'Click to publish'}
            >
              <Icon name={game?.published ? 'Globe' : 'Lock'} size={10} />
              {game?.published ? 'Public' : 'Private'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}